/**
 * Features MVI Effects
 *
 * Side effects: API calls, dispatches intents on completion.
 * app-shell-work-context-R04/R07; business-model-features-R08/R11.
 */

import type {
  ICreateFeatureRequest,
  IFeature,
  IUpdateFeatureRequest,
  TFeatureId,
} from "@/shared/types";
import type { IApiError } from "@/shared/types";
import { getFeaturesApiClient } from "@/shared/api";
import { intents } from "./intents";
import type { TFeaturesIntent } from "./intents";

type Dispatch = (intent: TFeaturesIntent) => void;

function createApiError(error: unknown): IApiError {
  if (
    error !== null &&
    typeof error === "object" &&
    "response" in error &&
    error.response !== null &&
    typeof error.response === "object"
  ) {
    const response = error.response;
    if ("data" in response && response.data !== null && typeof response.data === "object") {
      const data = response.data;
      const code =
        "code" in data && typeof data.code === "string" ? data.code : "UNKNOWN_ERROR";

      let message = "Request failed";
      if ("message" in data && typeof data.message === "string") {
        message = data.message;
      } else if ("error" in data && typeof data.error === "string") {
        message = data.error;
      } else if (error instanceof Error) {
        message = error.message;
      }
      const params =
        "params" in data && typeof data.params === "object" && data.params !== null
          ? (data.params as Record<string, unknown>)
          : undefined;

      return { code, message, params };
    }
  }

  if (error instanceof Error) {
    return { code: "UNKNOWN_ERROR", message: error.message };
  }
  return { code: "UNKNOWN_ERROR", message: String(error) };
}

export async function loadFeatures(
  dispatch: Dispatch,
  projectId: string,
  selectedFeatureId = "",
  isCurrent: () => boolean = () => true,
): Promise<void> {
  dispatch(intents.loadFeaturesStart());
  try {
    const api = getFeaturesApiClient();
    const features = await api.listByProject(projectId);
    if (!isCurrent()) return;
    // app-shell-work-context-R04; business-model-features-R08/R11:
    // a live archived choice can predate the privilege to read its label.
    let retainedFeature: IFeature | null = null;
    if (selectedFeatureId.length > 0 && !features.some(feature => feature.id === selectedFeatureId)) {
      const withArchived = await api.listByProject(projectId, true);
      if (!isCurrent()) return;
      retainedFeature = withArchived.find(feature => feature.id === selectedFeatureId &&
        feature.projectId === projectId && feature.status === 'archived' && !feature.isDeleted) ?? null;
    }
    // Перечень приходит вместе со своим проектом: выбор фичи принадлежит
    // проекту, и пересчитать его можно, только зная, чей это перечень
    // (business-model-features-R10).
    dispatch(intents.loadFeaturesSuccess(projectId, features, retainedFeature));
  } catch (error: unknown) {
    if (isCurrent()) dispatch(intents.loadFeaturesError(createApiError(error)));
  }
}

export async function createFeature(
  dispatch: Dispatch,
  projectId: string,
  request: ICreateFeatureRequest,
  canReadList = true,
): Promise<void> {
  dispatch(intents.createFeatureStart());
  try {
    const api = getFeaturesApiClient();
    const feature = await api.create(projectId, request);
    dispatch(intents.createFeatureSuccess(feature));
    // Перечень фич перечитывается (feature-lifecycle-R03).
    if (canReadList) await loadFeatures(dispatch, projectId);
  } catch (error: unknown) {
    dispatch(intents.createFeatureError(createApiError(error)));
  }
}

/**
 * Перечень вместе с архивными — для поверхности управления фичами
 * (feature-lifecycle-R28). Базовый перечень он не подменяет: селектор фичи и
 * рабочий контекст продолжают жить перечнем без архивных (features-R01, R11).
 */
export async function loadFeaturesWithArchived(
  dispatch: Dispatch,
  projectId: string,
): Promise<void> {
  dispatch(intents.loadFeaturesStart());
  try {
    const api = getFeaturesApiClient();
    const features = await api.listByProject(projectId, true);
    dispatch(intents.loadWithArchivedSuccess(features));
  } catch (error: unknown) {
    dispatch(intents.loadFeaturesError(createApiError(error)));
  }
}

/**
 * Переименование, изменение описания и смена статуса — одно изменение
 * (feature-lifecycle-R04, R09). Успех перечитывает перечень фич и подпись
 * фичи в селекторе (R08).
 */
export async function updateFeature(
  dispatch: Dispatch,
  projectId: string,
  featureId: TFeatureId,
  request: IUpdateFeatureRequest,
): Promise<void> {
  dispatch(intents.updateFeatureStart());
  try {
    const api = getFeaturesApiClient();
    const feature = await api.update(projectId, featureId, request);
    dispatch(intents.updateFeatureSuccess(feature));
    await loadFeatures(dispatch, projectId);
  } catch (error: unknown) {
    dispatch(intents.updateFeatureError(createApiError(error)));
  }
}

/**
 * Удаление фичи (feature-lifecycle-R11–R17). Отказ по удержанию приходит от
 * продукта с признаком причины и параметрами — клиент их только показывает
 * (R13, R24). После успеха перечень перечитывается (R17).
 */
export async function deleteFeature(
  dispatch: Dispatch,
  projectId: string,
  featureId: TFeatureId,
): Promise<void> {
  dispatch(intents.deleteFeatureStart());
  try {
    const api = getFeaturesApiClient();
    await api.remove(projectId, featureId);
    dispatch(intents.deleteFeatureSuccess(featureId));
    await loadFeatures(dispatch, projectId);
  } catch (error: unknown) {
    dispatch(intents.deleteFeatureError(createApiError(error)));
  }
}
